(ns graph-ui.web.push
  "NOTIFICATIONS ON THIS DEVICE — the client half of web push
   (IKC3/PLAN-ikc-v3-082-01/WD-PUSH, decision D9).

   ;; @plan PLAN-ikc-v3-082-01-the-warden
   ;; @principle PRINCIPLE-ikc-v3-014-01-programmability

   A button of the UI-DSL declares `:effect :enable-push`. The renderer
   (graph-ui.web.widgets/widget-button) calls `enable!` on a click, and then
   sends the command of the button with the answer in `$push-subscription`.
   So the button is declared data, and the command is the one a script sends.

   THE STEPS, in the order that a browser needs them.
   1. Ask for the permission. This call must run in the click itself, so it
      is the first step and no step comes before it.
   2. Wait for the service worker (resources/public/web/sw.js).
   3. Ask the bus for the public key of the sender (:contact/push-key).
   4. Subscribe with pushManager, or take the subscription the browser has.
   5. Give back the JSON of the subscription.

   EACH EFFECT IS A KEY OF `fx`, so a test in node drives the steps with no
   window and no service worker (test/cljs/graph-ui/web/push_test.cljs)."
  (:require [clojure.string :as str]))

(defn b64u->bytes
  "PURE: the Uint8Array of base64url text `s`, with or without padding. The
   browser needs the key of the sender in this form (applicationServerKey)."
  [s]
  (let [pad (apply str (repeat (mod (- 4 (mod (count s) 4)) 4) "="))
        b64 (-> (str s pad) (str/replace "-" "+") (str/replace "_" "/"))
        raw (js/atob b64)
        out (js/Uint8Array. (.-length raw))]
    (dotimes [i (.-length raw)] (aset out i (.charCodeAt raw i)))
    out))

(defn device-label
  "PURE: a short name for a device from its user agent text, for the person
   to tell two devices apart. Example: \"Android, Chrome\"."
  [ua]
  (let [ua  (or ua "")
        os  (cond (re-find #"Android" ua)         "Android"
                  (re-find #"iPhone|iPad" ua)     "iOS"
                  (re-find #"Windows" ua)         "Windows"
                  (re-find #"Mac OS X|Macintosh" ua) "macOS"
                  (re-find #"Linux|X11" ua)       "Linux"
                  :else                           "Device")
        app (cond (re-find #"Firefox/" ua)        "Firefox"
                  (re-find #"Edg/" ua)            "Edge"
                  (re-find #"Chrome/" ua)         "Chrome"
                  (re-find #"Safari/" ua)         "Safari"
                  :else                           nil)]
    (if app (str os ", " app) os)))

(def not-supported
  "The sentence for a browser that cannot receive push messages."
  (str "This browser cannot show notifications. "
       "On an iPhone, add the app to the Home Screen first, and open it from there."))

(def denied
  "The sentence for a permission that is not granted."
  (str "Notifications are not allowed. "
       "Allow them for this site in the settings of the browser, and press the button again."))

(defn- supported? []
  (boolean (and (exists? js/Notification)
                (exists? js/navigator)
                (.-serviceWorker js/navigator)
                (exists? js/PushManager))))

(defn- ask-permission! []
  (js/Promise. (fn [resolve _]
                 ;; Older browsers take a callback, newer ones return a promise.
                 (let [p (.requestPermission js/Notification resolve)]
                   (when (and p (.-then p)) (.then p resolve))))))

(defn- registration! [] (.-ready (.-serviceWorker js/navigator)))

(defn same-key?
  "PURE: is `existing` (an ArrayBuffer, a typed array or nil, the
   applicationServerKey of a subscription) the same key as `key-b64u`, the
   key of the bus as base64url text? A nil key is not the same."
  [existing key-b64u]
  (boolean
   (when existing
     (let [a (js/Uint8Array. existing)
           b (b64u->bytes key-b64u)]
       (and (= (.-length a) (.-length b))
            (every? #(= (aget a %) (aget b %)) (range (.-length a))))))))

(defn- subscribe!
  "Subscribe with pushManager. A subscription that the browser has for ANOTHER
   key is removed first. After a rotation of the VAPID key, the old
   subscription would give a push service that refuses the new signature."
  [reg key-bytes key-b64u]
  (let [pm  (.-pushManager reg)
        new! #(.subscribe pm #js {:userVisibleOnly true :applicationServerKey key-bytes})]
    (-> (.getSubscription pm)
        (.then (fn [existing]
                 (cond
                   (nil? existing) (new!)
                   (same-key? (some-> existing .-options .-applicationServerKey) key-b64u) existing
                   :else (-> (.unsubscribe existing) (.then new!)))))
        (.then (fn [sub] (js->clj (.toJSON sub) :keywordize-keys true))))))

(defn- key-reply
  "PURE: the public key, or the error sentence, from the reply of
   :contact/push-key. The reply is a map, or a vector that holds one."
  [reply]
  (let [r (if (sequential? reply) (first reply) reply)]
    (if-let [k (:public-key r)]
      {:key k}
      {:error (or (:message r) "The bus gave no key for notifications.")})))

(defn enable!
  "Turn notifications on for this device. `bus` is the bus client. `done` is
   called once, with {:subscription m :device-label s} or {:error sentence}.
   `fx` replaces an effect: :supported?, :ask-permission!, :registration!,
   :push-key! (fn [bus cb]), :subscribe! (fn [reg key-bytes key-b64u]) and :ua."
  ([bus done] (enable! bus done {}))
  ([bus done fx]
   (let [{:keys [supported? ask-permission! registration! push-key! subscribe! ua]
          :or   {supported?       supported?
                 ask-permission!  ask-permission!
                 registration!    registration!
                 subscribe!       subscribe!
                 push-key!        (fn [b cb] (js-invoke b "dispatch" {:type :contact/push-key} cb))
                 ua               (when (exists? js/navigator) (.-userAgent js/navigator))}} fx
         fail (fn [e]
                (done {:error (if (string? e)
                                e
                                (str "Notifications failed: " (or (some-> e .-message) e)))}))
         step3 (fn [reg]
                 (push-key! bus
                            (fn [reply]
                              (let [{k :key e :error} (key-reply reply)]
                                (if e
                                  (done {:error e})
                                  (-> (subscribe! reg (b64u->bytes k) k)
                                      (.then (fn [sub]
                                               (done {:subscription sub
                                                      :device-label (device-label ua)})))
                                      (.catch fail)))))))]
     (if-not (supported?)
       (done {:error not-supported})
       ;; THE PERMISSION FIRST, in the click. Every later step may be late.
       (-> (ask-permission!)
           (.then (fn [perm]
                    (if (= "granted" perm)
                      (-> (registration!) (.then step3))
                      (done {:error denied}))))
           (.catch fail))))))

(defn result-note
  "PURE: the text beside a button whose answer is a count, or nil. `show`
   is {:key k :says s} of the prop :show-result. The text is s and the value
   of k in the first event of `reply` that holds k. A reply is a map or a
   vector of maps."
  [show reply]
  (when-let [k (some-> (or (:key show) (get show "key")) keyword)]
    (let [says (or (:says show) (get show "says") "")
          evs  (if (sequential? reply) reply [reply])
          ev   (some #(when (and (map? %) (contains? % k)) %) evs)]
      (when ev (str says (get ev k))))))
